import { Button, Icon, Tooltip } from "@resource/atlas";
import { atlasCircleQuestion } from "@resource/atlas/icons";
import clsx from "clsx";
import React, { FunctionComponent, useState } from "react";

const PREFERRED_SHARE_PRICE = 2.21;

export type OfferProps = {
  descriptionHtml?: string;
  header?: string;
  candidateName: string;
  jobName: string;
  offer: {
    totalComp: string;
    salary: string;
    optionCount: string;
    isContractor?: string;
    bonus?: string;
    extras?: string;
    level?: string;
    url?: string;
    quota?: string;
    commission?: string;
  };
};

type OfferInfoRowProps = {
  label: string;
  info: string;
  variant?: "quantity" | "label";
};

function OfferInfoRow({ label, info, variant = "label" }: OfferInfoRowProps) {
  return (
    <div className="flex py-[.625rem] space-x-2">
      <p
        className={clsx(
          "text-body-md-heavy",
          variant === "quantity" ? "flex-1" : "w-28",
          "flex-shrink-0"
        )}
      >
        {label}
      </p>
      <p
        className={clsx("text-body-md", {
          "flex-1": variant === "quantity",
        })}
      >
        {info}
      </p>
    </div>
  );
}

type OfferInfoProps = {
  variant?: "h1" | "h2";
  header: string;
  subtext: string;
  info?: string;
  icon?: {
    emoji: string;
    bgClassName: string;
  };
};

function OfferInfoTile({
  variant = "h2",
  header,
  subtext,
  info,
  icon,
}: OfferInfoProps) {
  const Title = variant === "h1" ? "h1" : "h2";
  return (
    <div className="flex flex-1 space-x-2 items-start">
      {icon && (
        <div
          className={clsx(
            "flex items-center justify-center flex-shrink-0",
            `${icon.bgClassName} rounded-full w-[3.25rem] h-[3.25rem]`
          )}
        >
          {icon.emoji}
        </div>
      )}
      <div>
        <Title className={variant === "h1" ? "text-h1" : "text-h2"}>
          {header}
        </Title>
        <div className="flex items-center space-x-[.375rem]">
          <p className="text-body-md-heavy text-subtle">{subtext}</p>
          {info && (
            <Tooltip content={info} isInstant>
              <Icon
                size="small"
                content={atlasCircleQuestion}
                className="text-subtle"
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

type Scenario = 1 | 10 | 100 | 1000;

type EquityScenarioProps = {
  scenario: Scenario;
  selectedScenario: Scenario;
  onSelect(scenario: Scenario): void;
};

function EquityScenario({
  scenario,
  selectedScenario,
  onSelect,
}: EquityScenarioProps) {
  return (
    <div
      role="button"
      tabIndex={-10}
      onKeyUp={() => onSelect(scenario)}
      onClick={() => onSelect(scenario)}
      className={clsx("cursor-pointer py-[.625rem] px-4", {
        "bg-light-gray-100": selectedScenario !== scenario,
        "bg-red-50": selectedScenario === scenario,
        "text-red-500": selectedScenario === scenario,
      })}
    >
      <p className="text-body-md">{scenario}x</p>
    </div>
  );
}

const Card: React.FC = ({ children }) => (
  <div className="shadow-sm rounded-md p-6 space-y-6">{children}</div>
);

const InfoRowContainer: React.FC = ({ children }) => (
  <div className="divide-y divide-light-gray-600">{children}</div>
);

const TileContainer: React.FC = ({ children }) => (
  <div className="space-y-4">{children}</div>
);

const Offer: FunctionComponent<OfferProps> = ({
  header,
  descriptionHtml,
  jobName,
  candidateName,
  offer,
}) => {
  const nf = new Intl.NumberFormat("en-US");
  const [showingOffer, setShowingOffer] = useState(false);
  const [selectedScenario, setSelectedMultiplier] = useState<Scenario>(1);
  if (!descriptionHtml && !header && !offer) {
    return null;
  }

  const showContractorOffer = offer.isContractor === "true";

  const computeEquityValue = (shares: number, multiplier: number) =>
    Math.round(shares * PREFERRED_SHARE_PRICE) * multiplier;

  return (
    <div className="space-y-8 mb-6">
      <Card>
        {!showingOffer ? (
          <div className="flex items-center justify-center h-24">
            <Button variant="primary" onClick={() => setShowingOffer(true)}>
              View your offer!
            </Button>
          </div>
        ) : (
          <>
            <p className="text-h5 text-subtle">Compensation</p>
            <div className="space-y-1">
              <p className="text-body-md text-subtle">{candidateName}</p>
              <p className="text-h3">{jobName}</p>
            </div>
            <OfferInfoTile
              header={`$${nf.format(Number(offer.totalComp))}`}
              variant="h1"
              subtext="Total Annual Compensation (appx)"
              info="Your total cash, benefits and current equity value"
            />
            <TileContainer>
              <div className="flex">
                <OfferInfoTile
                  header={`$${nf.format(Number(offer.salary))}`}
                  subtext="Base salary"
                  info="Your annual cash compensation"
                />
                <OfferInfoTile
                  header={showContractorOffer ? "$1,200" : "$9,100"}
                  subtext="Benefits & perks"
                  info={
                    showContractorOffer
                      ? "Wellness and personal growth"
                      : "Health insurance, wellness and personal growth"
                  }
                />
              </div>
              <div className="flex">
                <OfferInfoTile
                  header={`${nf.format(Number(offer.optionCount))}`}
                  subtext="Options (over 4 yrs)"
                  info="Your total number of stock options"
                />
                <OfferInfoTile
                  header={`$${nf.format(
                    computeEquityValue(Number(offer.optionCount), 1)
                  )}`}
                  subtext="Equity value today (appx)"
                  info="Your options multiplied by our preferred share price of $2.21"
                />
              </div>
            </TileContainer>
            {(offer.quota ||
              offer.commission ||
              offer.extras ||
              offer.bonus ||
              offer.level) && (
              <InfoRowContainer>
                {offer.level && (
                  <OfferInfoRow
                    variant="label"
                    label="Level"
                    info={offer.level}
                  />
                )}
                {offer.bonus && (
                  <OfferInfoRow
                    variant="label"
                    label="Bonus"
                    info={offer.bonus}
                  />
                )}
                {offer.quota && (
                  <OfferInfoRow
                    variant="label"
                    label="Quota"
                    info={offer.quota}
                  />
                )}
                {offer.commission && (
                  <OfferInfoRow
                    variant="label"
                    label="Commission"
                    info={offer.commission}
                  />
                )}
                {offer.extras && (
                  <OfferInfoRow
                    variant="label"
                    label="Extras"
                    info={offer.extras}
                  />
                )}
              </InfoRowContainer>
            )}
            <div className="space-y-4">
              <p className="text-body-md">What&apos;s my equity worth?</p>
              <div className="flex justify-start rounded-md space-x-[.0625rem] overflow-hidden">
                <EquityScenario
                  scenario={1}
                  selectedScenario={selectedScenario}
                  onSelect={setSelectedMultiplier}
                />
                <EquityScenario
                  scenario={10}
                  selectedScenario={selectedScenario}
                  onSelect={setSelectedMultiplier}
                />
                <EquityScenario
                  scenario={100}
                  selectedScenario={selectedScenario}
                  onSelect={setSelectedMultiplier}
                />
                <EquityScenario
                  scenario={1000}
                  selectedScenario={selectedScenario}
                  onSelect={setSelectedMultiplier}
                />
              </div>
              <InfoRowContainer>
                <OfferInfoRow
                  variant="quantity"
                  label="Equity value"
                  info={`$${nf.format(
                    computeEquityValue(
                      Number(offer.optionCount),
                      selectedScenario
                    )
                  )}`}
                />
              </InfoRowContainer>
            </div>
          </>
        )}
      </Card>
      <Card>
        <p className="text-h5 text-subtle">Benefits &amp; Perks</p>
        <TileContainer>
          {!showContractorOffer && (
            <div className="flex">
              <OfferInfoTile
                header="100%"
                subtext="Health insurance"
                icon={{
                  bgClassName: "bg-orange-50",
                  emoji: "🏥",
                }}
              />
              <OfferInfoTile
                header="100%"
                subtext="Dental insurance"
                icon={{
                  bgClassName: "bg-green-50",
                  emoji: "😬",
                }}
              />
            </div>
          )}
          <div className="flex">
            {showContractorOffer ? (
              <OfferInfoTile
                header="100%"
                subtext="Fully Remote"
                icon={{
                  bgClassName: "bg-red-50",
                  emoji: "💻",
                }}
              />
            ) : (
              <OfferInfoTile
                header="100%"
                subtext="Vision insurance"
                icon={{
                  bgClassName: "bg-red-50",
                  emoji: "👀",
                }}
              />
            )}
            <OfferInfoTile
              header="Unlimited"
              subtext="Time-off (PTO/Sick)"
              icon={{
                bgClassName: "bg-blue-50",
                emoji: "🏖",
              }}
            />
          </div>
        </TileContainer>

        <InfoRowContainer>
          {!showContractorOffer && (
            <OfferInfoRow label="Office" info="Fully remote" />
          )}
          {!showContractorOffer && (
            <OfferInfoRow
              label="Computer"
              info="Brand new MacBook Pro or equivalent setup"
            />
          )}
          <OfferInfoRow
            label="Equipment"
            info="Whatever you need to be productive at home"
          />
          <OfferInfoRow label="Growth" info="Monthly learning & books budget" />
        </InfoRowContainer>
      </Card>
    </div>
  );
};

export default Offer;
